import React, { useState, useEffect, useRef } from 'react';
import './ManningInfo.css';
import logo from './assets/dg-logo.png';
import userIcon from './assets/logout.png';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { ICellRendererParams } from 'ag-grid-community'; // For type safety
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import SlidingDrawer from './SlidingDrawer';
import Navigation from './Navigation'; // Import the new Navigation component
import { useParams } from 'react-router-dom';
import ManningModal from './ManningModal'; // Import the new modal component
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth, fetchWithAuthV2 } from './utils';
import Select from 'react-select';
import { parseISO, addWeeks, format } from 'date-fns';
import filterIcon from './assets/filter.png';
import Modal from "./Modal";
import { useMemo } from 'react';
import { ReactComponent as SelectAllIcon } from './assets/select_all.svg';
import { ReactComponent as DeselectAllIcon } from './assets/deselect_all.svg';
import personSearch from './assets/person_search.svg';
import { ReactComponent as SearchIcon } from './assets/search.svg';

interface Region {
  company_code: string;
  region_code: string;
  region_name: string;
}

interface Office {
  company_code: string;
  region_code: string;
  office_code: string;
  office_name: string;
}

interface Manning {
  manning_info_id: string; 
  company_code: string;
  region_code: string;
  office_code: string;
  name: string;
  email: string;
  title: string;
  type: string;
  primary_practice: string;
  discipline: string;
  experience: string;
  current_market_sector: string;
  leader_name: string;
  project_name: string;
  project_start_date: string;
  project_end_date: string;
  utilization_percentage: number | string;
}

interface ManningWeekData {
  [weekKey: string]: {
    projectCode: string | null;
    projectName: string;
    backgroundColor?: string;
    manning_project_info_id: number | null;
    utilization_percentage?: string | number;
  };
}

interface Project {
  project_code: string;
  project_name: string;
  client_name: string;
}


// Utility functions for caching
const getCachedData = (key: string) => {
  const cached = localStorage.getItem(key);
  if (cached && cached !== "undefined") {
    try {
      return JSON.parse(cached);
    } catch (e) {
      console.error(`Error parsing cached data for key: ${key}`, e);
    }
  }
  return null;
};


const setCachedData = (key: string, data: any) => {
  localStorage.setItem(key, JSON.stringify(data));
};

const getEffectiveRegionCode = (region_code: string | undefined) => {
  return region_code === "NATIONAL" ? "IS-All" : region_code || "";
};

const ManningInfo: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [manningData, setManningData] = useState<Manning[]>([]);
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const [quickFilterText, setQuickFilterText] = useState<string>(''); // Quick filter text
  const [gridApi, setGridApi] = useState<any>(null); // State for grid API
  const [selectedSubLink, setSelectedSubLink] = useState<'manning' | 'opportunity' | 'project' | 'openpros' | 'recruiting'>('manning');
  const [selectedOffices, setSelectedOffices] = useState<string[]>([]);
  const [isHovered, setIsHovered] = useState<boolean>(false); // State for user icon hover
  const [loggedInUser, setLoggedInUser] = useState<string>('John Doe'); // Example user name
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);
  const [calendarPosition, setCalendarPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [selectedColorLabel, setSelectedColorLabel] = useState<string | null>(null);

  const [selectedRowData, setSelectedRowData] = useState<Manning | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const drawerRef = useRef<HTMLDivElement>(null); // Reference to the SlidingDrawer
  const { regionCode } = useParams<{ regionCode: string }>(); 
  const [loading, setLoading] = useState<boolean>(false); 
  const [loadingClear, setLoadingClear] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false); // New state for modal
  const calendarRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<'list' | 'sheet'>('sheet'); 
  const [manningWeeklyData, setManningWeeklyData] = useState<Record<string, ManningWeekData>>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [visibleWeekStart, setVisibleWeekStart] = useState(0);
  const totalWeeks = 48;
  const weeksPerPage = 6; // Show 6 weeks at a time
  const [currentPage, setCurrentPage] = useState(0); // Start at page 0
  const pageSize = 50; // 50 records per page
  const [variousModalOpen, setVariousModalOpen] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<{ projectCode: string; projectName: string; utilization?: number }[]>([]);
  const [selectedUtilization, setSelectedUtilization] = useState<number>(0);
  const [selectedPersonId, setSelectedPersonId] = useState<string>(''); // to know which manning id we're editing
  const [selectedWeekKey, setSelectedWeekKey] = useState<string>(''); 
  const [selectedNewProject, setSelectedNewProject] = useState<{ projectCode: string; projectName: string } | null>(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectUtilization, setProjectUtilization] = useState<string>('100');
  const [projectEndDate, setProjectEndDate] = useState<Date | null>(null);
  const [projectStartDate, setProjectStartDate] = useState<Date | null>(null);
  const [error, setError] = useState<string>('');
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [showPopover, setShowPopover] = useState<boolean>(false); // Popover visibility
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [popoverData, setPopoverData] = useState<string[]>([
    'How to set a project for a Pro with a single project?',
    'Select the week you want to start the project.',
    'From the project list for that week, select the desired project.',
    'A modal window will appear.',
    'Choose the Utilization percentage.',
    'Select the date until which you want to assign this project using the calendar.',
    'Click Save.',
    'The selected project will be saved, reflected in the Manning page, and the modal will close.',
    'How to set multiple projects for a Pro?',
    'Select the week you want to start the projects.',
    'From the project list for that week, select the "Various" project.',
    'A modal window will appear.',
    'Add each project one by one by selecting the project and its utilization.',
    'Once all projects are selected, choose the date until which you want to assign these projects using the calendar.',
    'Click Save.',
    'The selected projects will be saved, reflected in the Manning page, and the modal will close.',
    // Add the other questions and answers here
  ]);

  const [forecastedProjectName, setForecastedProjectName] = useState<string>('');

  const [selectedLeader, setSelectedLeader] = useState<string | null>(null);
  const [selectedPractice, setSelectedPractice] = useState<string | null>(null);
  // Generate unique dropdown options from the fetched manning data
  const uniqueLeaders = Array.from(new Set(manningData.map((m) => m.leader_name).filter(Boolean))).sort();
  const uniquePractices = Array.from(new Set(manningData.map((m) => m.primary_practice).filter(Boolean))).sort();
  const uniqueTitles = Array.from(new Set(manningData.map((m) => m.title).filter(Boolean))).sort();
  const [showFilters, setShowFilters] = useState(false);
  const [showUtilization, setShowUtilization] = useState(true);
  const [showLocation, setShowLocation] = useState(true);
  const [showLeaderPractice, setShowLeaderPractice] = useState(true);
  const [selectedManningName, setSelectedManningName] = useState<string>('');
  const [editingManning, setEditingManning] = useState<Manning | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set());
  const [showTitleFilter, setShowTitleFilter] = useState(true);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [projectSearchLoading, setProjectSearchLoading] = useState(false);
  const [showProjectFilter, setShowProjectFilter] = useState(true);
  const [projectFilterText, setProjectFilterText] = useState('');
  const [clearTriggered, setClearTriggered] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [manningToDelete, setManningToDelete] = useState<Manning | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState<string | null>(null);

  // Per-item pending selection for the “add” row
  const [pendingProject, setPendingProject] = useState<{ projectCode: string; projectName: string } | null>(null);
  const [pendingUtilization, setPendingUtilization] = useState<number | null>(null);
  const [showHelpVideo, setShowHelpVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [showExperienceFilter, setShowExperienceFilter] = useState(true);

  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [manningSkillData, setManningSkillData] = useState<any[]>([]);
  const [showSkillFilter, setShowSkillFilter] = useState(true);
  const [populatingWeeks, setPopulatingWeeks] = useState(false);
  const [populateWeeksStatus, setPopulateWeeksStatus] = useState<string>('');


  const handleHelpClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopoverPosition({
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX,
    });
    setShowPopover(true);
  };


  const [reportModalOpen,setReportModalOpen]=useState(false);
  const [reportWeeks,setReportWeeks]=useState(3);
  const [reportUtilization,setReportUtilization]=useState(0);
  const [reportDownloading,setReportDownloading]=useState(false);

  const today = new Date();
  const day = today.getDay();

  const [reportPractice,setReportPractice] = useState("CSI");
  const [includeCurrentWeek,setIncludeCurrentWeek] = useState(day >= 1 && day <= 3);


  const getTargetWeekDate = () => {
    const d = new Date();
    const monday = new Date(d);
    monday.setDate(d.getDate() + (d.getDay() === 0 ? -6 : 1 - d.getDay()));

    const weeksToAdd = includeCurrentWeek ? reportWeeks - 1 : reportWeeks;
    monday.setDate(monday.getDate() + weeksToAdd * 7);

    return `${String(monday.getMonth()+1).padStart(2,'0')}-${String(monday.getDate()).padStart(2,'0')}`;
  };
  const handlePopoverClose = () => {
    setShowPopover(false);
  };

  const experienceRanges = [
    "0-2",
    "3-5",
    "6-8",
    "9-12",
    "13-16",
    "17-20",
    "20+"
  ];

  // Fetch regions when the component mounts
  useEffect(() => {
    const fetchRegions = async () => {
      const cachedRegions = getCachedData('regions_new');
      if (cachedRegions) {
        setRegions(cachedRegions);
        return;
      }

      try {
        const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_regions&company_code=DG`;
        const data = await fetchWithAuth(url);

        setRegions(data);
        setCachedData('regions_new', data); // Cache the data
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    };

    fetchRegions();
  }, []);

  // Fetch offices and manning data when the regionCode changes
  useEffect(() => {
    if (regionCode) {
      fetchOffices(regionCode);
    }
  }, [regionCode]);

  const projectOptions = projects.map((project) => ({
    value: project.project_code,
    label: `${project.project_code} - ${project.client_name} - ${project.project_name}`,
  }));

  const fetchOffices = async (region_code: string) => {
    const cachedOffices = getCachedData(`offices_${region_code}`);
    if (cachedOffices) {
      setOffices(cachedOffices);
      setSelectedOffices(cachedOffices.map((office: Office) => office.office_code));
  
      fetchProjects(getEffectiveRegionCode(regionCode)); // ✅ Fetch projects even for cached offices
      fetchManningData(region_code, cachedOffices.map((office: Office) => office.office_code).join('|'), false, "", "");
  
      return;
    }
  
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_offices&company_code=DG&region_code=${region_code}`;
      const data = await fetchWithAuth(url);
  
      setOffices(data);
      setSelectedOffices(data.map((office: Office) => office.office_code));
      setCachedData(`offices_${region_code}`, data);
  
      fetchProjects(region_code); // ✅ Fetch projects after fetching offices
      fetchManningData(region_code, data.map((office: Office) => office.office_code).join('|'), false, "", "");
    } catch (error) {
      console.error('Error fetching offices:', error);
    }
  };
  

  const fetchManningData = async (region_code: string, office_codes: string, force: boolean, project_end_date: string, utilization: string) => {
    if (viewMode === 'list') {
      console.log("Fetching List View Manning Info...");
  
      const cachedManningData = getCachedData(`manning_${region_code}_${office_codes}`);
      if (!force && cachedManningData) {
        setManningData(cachedManningData);
        // For List View, maybe you don't need to fetch weekly data
        return cachedManningData;
      }
  
      try {
        setLoading(true);
        console.info(project_end_date)
        const action = project_end_date ? "get_filtered_manning_info" : "get_all_manning";
        const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=${action}&company_code=DG&region_code=${region_code}&office_codes=${office_codes}&project_start_date=${project_end_date}&util_percentage=${utilization}`;
        
        const data = await fetchWithAuth(url);
  
        setManningData(data);
        console.log('List View Manning Data:', data);
        setCachedData(`manning_${region_code}_${office_codes}`, data);
        return data;
      } catch (error) {
        console.error('Error fetching list view manning data:', error);
      } finally {
        setLoading(false);
      }
  
    } else if (viewMode === 'sheet') {
      console.log("Fetching Sheet1 View Manning Info...", project_end_date);
  
      const effectiveRegionCode = region_code === "NATIONAL" ? "IS-All" : region_code;
      // For Sheet View, you probably already have a separate call
      // like batch fetching Manning IDs for weekly projects
      const cachedManningData = getCachedData(`manning_${effectiveRegionCode}_${office_codes}`);
      if (!force && cachedManningData) {
        setManningData(cachedManningData);
        return cachedManningData;
      }
  
      try {
        setLoading(true);
        const action = project_end_date ? "get_filtered_manning_info" : "get_all_manning";
        const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=${action}&company_code=DG&region_code=${effectiveRegionCode}&office_codes=${office_codes}&project_start_date=${project_end_date}&util_percentage=${utilization}`;
  
        const data = await fetchWithAuth(url);
  
        setManningData(data);
        setCachedData(`manning_${effectiveRegionCode}_${office_codes}`, data);
        return data;
      } catch (error) {
        console.error('Error fetching sheet view manning data:', error);
      } finally {
        setLoading(false);
      }
    }
  };
  
  useEffect(() => {
    if (regionCode && selectedOffices.length > 0) {
      fetchManningData(regionCode, selectedOffices.join('|'), true, "", "");
    }
  }, [viewMode]);

  // ✅ Automatically clear filters and load default data on page navigation
useEffect(() => {
  const initializeData = async () => {
    // Wait until region and offices are ready before clearing
    if (regionCode && offices.length > 0) {
      console.log("Refreshing filter.. Please wait!");
      await clearFilter();
    }
  };
  initializeData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [regionCode, offices.length]);


// Refresh without clearing any filters
const refreshData = async () => {
  if (!regionCode || selectedOffices.length === 0) {
    console.error("Cannot refresh: regionCode or office selection missing.");
    return;
  }

  setLoading(true);
  try {
    const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_offices&company_code=DG&region_code=${regionCode}`;
    const data = await fetchWithAuth(url);
  

    setOffices(data);
    const newOfficeCodes = data.map((office: Office) => office.office_code);
    setSelectedOffices(newOfficeCodes);
    setCachedData(`offices_${regionCode}`, data);

    // figure out current filters
    let officeCodes = newOfficeCodes.join('|');
    /*
    setOffices(data);
    setSelectedOffices(data.map((office: Office) => office.office_code));
    setCachedData(`offices_${regionCode}`, data);

    // figure out current filters
    let officeCodes = selectedOffices.join('|');
    */
    let end = selectedEndDate ? selectedEndDate.toISOString().split('T')[0] : "";
    let util = selectedColorLabel ?? "";

    if (clearTriggered) {
      console.log("Refreshing after Clear Filter — ignoring filters");
      officeCodes = offices.map((o) => o.office_code).join('|');
      end = "";
      util = "";
      setSelectedLeader(null);
      setSelectedPractice(null);
      setSelectedTitle(null);
      setSelectedColorLabel(null);
      setSelectedColor(null);
      setSelectedDate(null);
      setSelectedEndDate(null);
    }


    // fetch manning list (respecting current filters)
    const effectiveRegion = getEffectiveRegionCode(regionCode);
    const newData = await fetchManningData(effectiveRegion, officeCodes, true, end, util);

    setClearTriggered(false);
    // if in sheet view, refresh the weekly data too (no clearing of filters/pages)
    if (viewMode === 'sheet' && Array.isArray(newData)) {
      // re-run batch fetch for current pagination without wiping state
      const totalPages = Math.ceil(newData.length / pageSize);
      const pageManningData = newData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

      // ensure we refetch visible page first
      await fetchManningWeeklyDataForPage(pageManningData, currentPage, /* force */ true);

      // and opportunistically refresh the rest
      for (let page = 0; page < totalPages; page++) {
        if (page === currentPage) continue;
        const chunk = newData.slice(page * pageSize, (page + 1) * pageSize);
        if (chunk.length > 0) {
          setTimeout(() => fetchManningWeeklyDataForPage(chunk, page, /* force */ true), page * 300);
        }
      }
    }
    fetchManningSkills(); // Refresh skills as well to ensure they are up to date
  } catch (err) {
    console.error("Error refreshing (preserving filters):", err);
  } finally {
    console.log("Refreshing Data.. Please wait!");
    setLoading(false);
  }
};

// Refresh without clearing any filters
const refreshDataAfterVarious = async () => {
  if (!regionCode || selectedOffices.length === 0) {
    console.error("Cannot refresh: regionCode or office selection missing.");
    return;
  }

  setLoading(true);
  try {
    // figure out current filters
    const officeCodes = selectedOffices.join('|');
    const end = selectedEndDate ? selectedEndDate.toISOString().split('T')[0] : "";
    const util = selectedColorLabel ?? "";

    // fetch manning list (respecting current filters)
    const effectiveRegion = getEffectiveRegionCode(regionCode);
    const newData = await fetchManningData(effectiveRegion, officeCodes, true, end, util);


    // if in sheet view, refresh the weekly data too (no clearing of filters/pages)
    if (viewMode === 'sheet' && Array.isArray(newData)) {
      // re-run batch fetch for current pagination without wiping state
      const totalPages = Math.ceil(newData.length / pageSize);
      const pageManningData = newData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

      // ensure we refetch visible page first
      await fetchManningWeeklyDataForPage(pageManningData, currentPage, /* force */ true);

      // and opportunistically refresh the rest
      for (let page = 0; page < totalPages; page++) {
        if (page === currentPage) continue;
        const chunk = newData.slice(page * pageSize, (page + 1) * pageSize);
        if (chunk.length > 0) {
          setTimeout(() => fetchManningWeeklyDataForPage(chunk, page, /* force */ true), page * 300);
        }
      }
    }
  } catch (err) {
    console.error("Error refreshing (preserving filters):", err);
  } finally {
    setLoading(false);
  }
};

const clearFilter = async () => {
  setFetchedPages(new Set()); // Clear fetched page tracker

  if (!regionCode) {
    console.error("Cannot refresh: regionCode or office selection missing.");
    return;
  }

  setLoadingClear(true);

  setQuickFilterText('');
  if (gridApi) {
    gridApi.setQuickFilter('');
  }
  setSelectedDate(null);
  setSelectedColor(null);
  setSelectedColorLabel(null);
  setProjectFilterText(''); 
  setSelectedLeader(null); 
  setSelectedPractice(null);
  setSelectedTitle(null); 
  setSelectedExperience(null);
  setSelectedSkill(null);
  setPopulateWeeksStatus('');

  // Fetch main manning data
  const newofficeCodes = offices.map((office) => office.office_code); // ✅ Get all offices
  setSelectedOffices(newofficeCodes); // ✅ Reset selected offices
  const officeCodes = newofficeCodes.join('|');
  localStorage.removeItem(`manning_${regionCode}_${officeCodes}`);
  const effectiveRegion = getEffectiveRegionCode(regionCode);
  const newData = await fetchManningData(effectiveRegion, officeCodes, true, "", "");


  if (viewMode === 'sheet' && Array.isArray(newData)) {
    const totalPages = Math.ceil(newData.length / pageSize);
    for (let page = 0; page < totalPages; page++) {
      const pageManningData = newData.slice(page * pageSize, (page + 1) * pageSize);
      setTimeout(() => {
        // Pass page index as 2nd arg, and force=true as 3rd arg
        fetchManningWeeklyDataForPage(pageManningData, page, true);
      }, page * 500);
    }
  }

  setLoadingClear(false);
  setClearTriggered(true);
};


  const toggleRegion = (regionCode: string) => {
    if (expandedRegion === regionCode) {
      setExpandedRegion(null);
    } else {
      setExpandedRegion(regionCode);
      setSelectedSubLink('manning');
      fetchOffices(regionCode);
    }
  };

  const handleOfficeChange = (officeCode: string, checked: boolean) => {
    const updatedOffices = checked
      ? [...selectedOffices, officeCode]
      : selectedOffices.filter((code) => code !== officeCode);
    setSelectedOffices(updatedOffices);

    if (expandedRegion) {
      fetchManningData(expandedRegion, updatedOffices.join('|'), false,"","");
    }
  };

  const onRowClicked = (data: Manning) => {
    setSelectedRowData(data);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedRowData) {
      const { name, value } = e.target;
      setSelectedRowData({ ...selectedRowData, [name]: value });
    }
  };

  const onLogout = () => {
    console.log('User logged out');
  };

  const handleAddNewManning = () => {
    setIsModalOpen(true);
  };

  const handleSaveNewManning = (newManning: any) => {
    setManningData([...manningData, newManning]);
    setIsModalOpen(false);
  };

  type ReactColDef = ColDef & {
    cellRendererFramework?: (params: ICellRendererParams) => JSX.Element | null;
  };
  
  const columnDefs: ColDef[] = [
      { headerName: 'Name', field: 'name', sortable: true, filter: true },
      {
        headerName: 'Project(s) - for next 12 weeks',
        field: 'project_name',
        minWidth: 300,
        maxWidth: 450,
        sortable: true,
        filter: true,
        cellRenderer: (params: ICellRendererParams) => {
          console.log(params.data) 
          const value = params.value;
          const manningInfoId = (params.data as any)?.manning_info_id;
          const manningProjectCode = (params.data as any)?.project_code;
          const manningProjectInfoId = (params.data as any)?.manning_project_info_id;
          if (!value) return null;
      
          // Check if the value is "Various" ONLY
          if (value.trim().includes('Various')){
            return (
              <a
                href="#"
                style={{ color: '#0d0d0eff', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault();
                  handleVariousHover(e, manningInfoId, manningProjectInfoId);
                }}
              >
                Various1
              </a>
            );
          }
      
          // Your existing logic: render color-coded project tags
          const projects = value.split(',').map((project: string, index: number) => {
            const [projectName, utilization] = project.split(' - ');
            let backgroundColor = '#ffffff';
            let textColor = '#000000';
      
            switch (utilization?.trim()) {
              case '100%':
                backgroundColor = '#ffffff';
                textColor = '#000000';
                break;
              case '75%':
                backgroundColor = '#70ad47';
                textColor = '#ffffff';
                break;
              case '50%':
                backgroundColor = '#ffe699';
                textColor = '#000000';
                break;
              case '25%':
                backgroundColor = '#ed7d31';
                textColor = '#ffffff';
                break;
              case '0%':
                backgroundColor = '#ffff00';
                textColor = '#000000';
                break;
              case 'NoPO':
                backgroundColor = '#8ea9db';
                textColor = '#000000';
                break;
            }
      
            const cleanProjectName = projectName.trim();
            const excludedNames = ['Onboarding', 'Open', 'PTO'];
            const label = excludedNames.includes(cleanProjectName)
              ? cleanProjectName
              : `${manningProjectCode} - ${cleanProjectName}`;
            
            return (
              <span
                key={index}
                style={{
                  display: 'inline-block',
                  backgroundColor,
                  color: textColor,
                  padding: '4px 8px',
                  margin: '2px',
                  borderRadius: '4px',
                  fontSize: '12px',
                }}
              >
                {label}
              </span>
            );
          });
      
          return <div>{projects}</div>;
        }
      },
      { headerName: 'Practice', field: 'primary_practice', sortable: true, filter: true },
      { headerName: 'Leader Name', field: 'leader_name', sortable: true, filter: true },
      { headerName: 'Office', field: 'office_code', sortable: true, filter: true },
  ];
  
  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const onQuickFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filterValue = event.target.value;
    setQuickFilterText(filterValue);

    if (gridApi) {
      if (typeof gridApi.setQuickFilter === 'function') {
        gridApi.setQuickFilter(filterValue);
      } else {
        console.error('Error: setQuickFilter is not a function on gridApi');
      }
    } else {
      console.error('Error: gridApi is null or undefined');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        setIsDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDrawerOpen]);

  const handleColorSelect = (color: string, label: string) => {
    if (isCalendarVisible && selectedColor === color) {
      // Toggle off if the same color is clicked again
      setIsCalendarVisible(false);
      setSelectedColor(null);
      return;
    }
  
    setSelectedColor(color);
    setSelectedColorLabel(label.replace('%', ''));
    setIsCalendarVisible(true);

    setSelectedDate(null);
  };
  

  const handleDateChange = (date: Date | null) => {
    if (date) { // Ensure date is not null
      setSelectedDate(date);
      setSelectedEndDate(date); 
      setIsCalendarVisible(false); // Hide calendar after date selection
      const colorLabel = selectedColorLabel ?? '';
      if (regionCode && selectedColor) {
        const formattedDate = date.toISOString().split('T')[0];
        const effectiveRegion = getEffectiveRegionCode(regionCode);
        fetchManningData(effectiveRegion, selectedOffices.join('|'), true, formattedDate, colorLabel);
      }

      setSelectedDate(null);
    }
  };
  
// Refresh data every 60 seconds
/*
useEffect(() => {
  const intervalId = setInterval(() => {
    refreshData();
  }, 300000); // 60000 milliseconds = 60 seconds

  return () => {
    clearInterval(intervalId); // Cleanup on unmount
  };
}, [regionCode, selectedOffices]); // Recreate interval when these dependencies change

*/
  const [username, setUsername] = useState('');
  const [showLogout, setShowLogout] = useState(false);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storageData = localStorage.getItem('userToken');
    if (storageData) {
      try {
        const parsed = JSON.parse(storageData);
        setUsername(parsed.username || '');
      } catch (error) {
        console.error('Failed to parse session userToken:', error);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarVisible(false);
      }
    };
  
    if (isCalendarVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
  
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarVisible]);
  

  const handleMouseEnter = () => {
    if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
    setShowLogout(true);
  };

  const handleMouseLeave = () => {
    logoutTimeoutRef.current = setTimeout(() => {
      setShowLogout(false);
    }, 200); // Allows user time to move mouse to Logout button
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("manning_")) {
        localStorage.removeItem(key);
      }
    });
    window.location.href = '/'; // or use navigate('/')
  };

  // Inside the ManningInfo component
  useEffect(() => {
    if (!isDrawerOpen) {
      // Call refreshData when the drawer is closed
      refreshData();
    }
  }, [isDrawerOpen]);  // Dependency array ensures this effect runs when isDrawerOpen changes


  const handleDrawerClose = () => {
    setIsDrawerOpen(false); // Close the drawer
  };

 
  const handleVariousHover = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    manningInfoId: string,
    manningProjectInfoId?: number
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();

    try {
      // Build URL with optional manning_project_info_id
      const base = `https://manningserviceapp.azurewebsites.net/api/MainRoute`;
      const qs =
        `?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==` +
        `&action=get_various_projects` +
        `&manning_id=${encodeURIComponent(manningInfoId)}` +
        (manningProjectInfoId != null ? `&manning_project_info_id=${encodeURIComponent(String(manningProjectInfoId))}` : '');

      const url = `${base}${qs}`;

      const data = await fetchWithAuth(url);
      setPopoverData(data);
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      });
      setShowPopover(true);
    } catch (error) {
      console.error('Error fetching various project data:', error);
    }
  };


  const handleVariousLeave = () => {
    setTimeout(() => setShowPopover(false), 200); // small delay so user can move mouse
  };

  const sheetViewColumnDefs: ColDef[] = [
    { headerName: 'Name', field: 'name', sortable: true, filter: true, width: 200 },
    ...Array.from({ length: totalWeeks }).map((_, index) => {
      const monday = new Date();
      monday.setDate(monday.getDate() + (1 - monday.getDay()) + (index * 7));
      const month = String(monday.getMonth() + 1).padStart(2, '0');
      const day = String(monday.getDate()).padStart(2, '0');
      const formattedDate = `${month}-${day}`;
  
      return {
        headerName: `${formattedDate}`,
        field: `week_${index + 1}`,
        width: 140,
        suppressSizeToFit: true,
        cellRenderer: (params: ICellRendererParams) => {
          const manningId = params.data.manning_info_id;
          const weekKey = `week_${index + 1}`;
  
          const currentWeekData = manningWeeklyData[manningId]?.[weekKey];
  
          return (
            <div style={{ minWidth: '120px' }}>
              <Select
                options={projectOptions}
                value={
                  currentWeekData
                    ? projectOptions.find((opt: { value: string; label: string }) => opt.value === currentWeekData.projectCode) || null
                    : null
                }
                onChange={(selectedOption) => {
                  if (!selectedOption) return;
                  handleProjectChange(manningId, weekKey, selectedOption.value);
                }}
                placeholder="Select"
                isSearchable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }), 
                  control: (base) => ({
                    ...base,
                    minHeight: '30px',
                    height: '30px',
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999, // Make sure dropdown is above
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    padding: 4,
                  }),
                  clearIndicator: (base) => ({
                    ...base,
                    padding: 4,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0px 6px',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),
                }}
              />
            </div>
          );
        },
      } as ColDef;
    }),
  ];
  
  const handleProjectChange = (manningId: string, weekKey: string, newProjectCode: string) => {
    const selectedProject = projectOptions.find((opt) => opt.value === newProjectCode);
    if (!selectedProject) return;
  
    // Find the manning name for the given manningId
    const manningObj = manningData.find((p) => p.manning_info_id === manningId);
    setSelectedManningName(manningObj?.name || '');

    const weekIndex = parseInt(weekKey.replace('week_', '')) - 1;
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setDate(weekStart.getDate() + (1 - weekStart.getDay()) + weekIndex * 7);
  
    setSelectedPersonId(manningId);
    setSelectedWeekKey(weekKey);

    if (selectedProject.label.toLowerCase().includes("various")) {
      // 🆕 If "Various", open the various modal
      setSelectedProjects([]);
      setSelectedUtilization(0);
      setProjectEndDate(null);
      setSelectedEndDate(null);
      setError('');
      setVariousModalOpen(true);
    } else {
      const weekData = manningWeeklyData[manningId]?.[weekKey];
      const isForecasted = selectedProject.value === 'Forecasted';
      // Normal project flow
      setSelectedNewProject({ projectCode: selectedProject.value, projectName: selectedProject.label });
      setForecastedProjectName(
        isForecasted && weekData?.projectName ? weekData.projectName : ''
      );
      if (selectedProject.value.toLowerCase() === 'open' || selectedProject.value.toLowerCase() === 'leave' || selectedProject.value.toLowerCase() === 'school' || selectedProject.value.toLowerCase() === 'not counted') {
        setProjectUtilization('0');
      } else {
        setProjectUtilization('100');
      }

      setProjectEndDate(null);
      setError('');
      setProjectModalOpen(true);
      setProjectStartDate(weekStart);
    }
  };
    
  const normalize = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const saveProjectAssignment = async () => {
    if (!projectEndDate) {
      setError('Please select an end date.');
      return;
    }
    setSaving(true);

    if (!selectedPersonId || !selectedWeekKey || !selectedNewProject || !projectEndDate) return;
  
    const manningWeeks = manningWeeklyData[selectedPersonId];
    const updatedWeeks: string[] = [];
  
    const startWeekIndex = parseInt(selectedWeekKey.replace('week_', '')) - 1;
  
    const today = new Date();
    const baseMonday = new Date(today);
    baseMonday.setDate(baseMonday.getDate() + (1 - baseMonday.getDay())); // start of current week
  
    // Monday for the selected start week
    const startMonday = new Date(baseMonday);
    startMonday.setDate(baseMonday.getDate() + startWeekIndex * 7);
  
    for (const [key, data] of Object.entries(manningWeeks)) {
      const weekIndex = parseInt(key.replace('week_', '')) - 1;
  
      // Monday for this week
      const weekMonday = new Date(baseMonday);
      weekMonday.setDate(baseMonday.getDate() + weekIndex * 7);
  
      // ✅ Only update if the week is >= selected start and <= selected end
      if (
        normalize(weekMonday) >= normalize(startMonday) &&
        normalize(weekMonday) <= normalize(projectEndDate) &&
        data.manning_project_info_id
      ) {
        try {
          const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_manning_project_info`;
          await fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify({
              manning_project_info_id: data.manning_project_info_id,
              projectCode: selectedNewProject.projectCode,
              projectName: selectedNewProject.projectCode === 'Forecasted'
                ? forecastedProjectName
                : selectedNewProject.projectName,
              util_percentage: projectUtilization,
            }),
          });
  
          updatedWeeks.push(key);
        } catch (error) {
          console.error(`❌ Error updating week ${key}:`, error);
        } finally {
          setSaving(false);
        }
      }

      //refreshData();
    }
  
    // ✅ Update local state
    setManningWeeklyData((prev) => {
      const updated = { ...prev };
      for (const weekKey of updatedWeeks) {
        updated[selectedPersonId][weekKey] = {
          ...updated[selectedPersonId][weekKey],
          projectCode: selectedNewProject.projectCode,
          projectName: selectedNewProject.projectCode === 'Forecasted' ? forecastedProjectName : selectedNewProject.projectName,
          utilization_percentage: projectUtilization,
        };
      }
      return updated;
    });
  
    setProjectModalOpen(false);
    setSelectedNewProject(null);
    setProjectEndDate(null);
    setForecastedProjectName('');

  
    // ✅ Update the main manning list
    setManningData((prevData) =>
      prevData.map((person) =>
        person.manning_info_id === selectedPersonId
          ? {
              ...person,
              utilization_percentage: projectUtilization === 'NoPO' ? 'NoPO' : parseInt(projectUtilization),
            }
          : person
      )
    );
  };
    
  
  const fetchManningWeeklyData = async (manningId: string) => {
    try {
      console.log('Fetching weekly data for', manningId);

      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_manning_project_info&manning_id=${manningId}`;
      const data = await fetchWithAuth(url);
  
      const weeks: ManningWeekData = {};
  
      const firstMonday = parseISO(data[0]?.project_start_date);
  
      for (let i = 0; i < totalWeeks; i++) {
        const weekStartDate = addWeeks(firstMonday, i);
        const formattedDate = format(weekStartDate, 'MM/dd/yyyy');
  
        const project = data[i] || {};
  
        weeks[`week_${i + 1}`] = {
          projectCode: project?.project_code || null,
          projectName: project?.project_name || '',
          backgroundColor: '',
          manning_project_info_id: project?.manning_project_info_id || null,
          utilization_percentage: project?.utilization_percentage || '100', // ✅ Now valid
        };
      
      }
  
      setManningWeeklyData(prev => ({
        ...prev,
        [manningId]: weeks
      }));
  
    } catch (error) {
      console.error('Error fetching manning weekly data', error);
    }
  };
  

  const fetchProjects = async (region_code: string) => {
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_projects&company_code=DG&region_code=${region_code}`;
      const data = await fetchWithAuth(url);
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchManningWeeklyDataForPage = async (
  manningPageData: Manning[],
  pageNum: number,
  force = false
) => {
  // Prevent duplicate fetches unless forced
  if (!force && fetchedPages.has(pageNum)) {
    return;
  }

  const idsToFetch: string[] = [];
  for (const manning of manningPageData) {
    if (force || !manningWeeklyData[manning.manning_info_id]) {
      idsToFetch.push(manning.manning_info_id);
    }
  }

  if (idsToFetch.length === 0) {
    console.log('No new Manning IDs to fetch.');
    setFetchedPages(prev => {
      const next = new Set(prev);
      next.add(pageNum);
      return next;
    });
    return;
  }

  try {
    const idsParam = idsToFetch.join(',');
    const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_manning_project_info_batch&manning_ids=${idsParam}`;
    const responseData = await fetchWithAuth(url);

    // ... rest of your code unchanged ...
    const groupedData: Record<string, any[]> = {};
    responseData.forEach((item: any) => {
      const manningId = item.manning_info_id;
      if (!groupedData[manningId]) {
        groupedData[manningId] = [];
      }
      groupedData[manningId].push(item);
    });

    const updatedData: Record<string, ManningWeekData> = {};

    Object.keys(groupedData).forEach((manningId) => {
      const items = groupedData[manningId];
      if (items.length === 0) return;

      const allStartDates = items.map((proj) => parseISO(proj.project_start_date));
      const firstMonday = new Date(Math.min(...allStartDates.map((d) => d.getTime())));
      firstMonday.setDate(firstMonday.getDate() - (firstMonday.getDay() + 6) % 7); // Align to past Monday

      const weeks: ManningWeekData = {};

      for (let i = 0; i < totalWeeks; i++) {
        const weekStartDate = addWeeks(firstMonday, i);
        const weekEndDate = addWeeks(firstMonday, i + 1);

        const matchingProject = items.find((project) => {
          const projectStart = parseISO(project.project_start_date);
          const projectEnd = parseISO(project.project_end_date);
          return projectStart <= weekEndDate && projectEnd >= weekStartDate;
        });

        weeks[`week_${i + 1}`] = {
          projectCode: matchingProject?.project_code || null,
          projectName: matchingProject?.project_name || '',
          backgroundColor: '', // color is computed in rendering
          manning_project_info_id: matchingProject?.manning_project_info_id || null,
          utilization_percentage: matchingProject?.utilization_percentage || '100',
        };
      }

      updatedData[manningId] = weeks;
    });

    setManningWeeklyData((prev) => ({
      ...prev,
      ...updatedData,
    }));

    setManningData((prev) =>
      prev.map((person) => {
        const weeks = updatedData[person.manning_info_id];
        if (!weeks) return person;

        const utilizations = Object.values(weeks)
          .map((w) => w?.utilization_percentage)
          .filter((u): u is string | number => !!u);

        const highestUtil = utilizations.length > 0 ? utilizations[0] : person.utilization_percentage;

        return {
          ...person,
          utilization_percentage: highestUtil,
        };
      })
    );

    // Mark this page as fetched
    setFetchedPages(prev => {
      const next = new Set(prev);
      next.add(pageNum);
      return next;
    });

  } catch (error) {
    console.error('Error in batch fetching Manning Weekly Data:', error);
  }
};
  
useEffect(() => {
  const totalPages = Math.ceil(filteredManningData.length / pageSize);
  for (let page = 0; page < totalPages; page++) {
    if (page === currentPage) continue; // skip current, that's handled by main effect
    const pageManningData = manningData.slice(page * pageSize, (page + 1) * pageSize);
    if (pageManningData.length > 0) {
      setTimeout(() => {
        fetchManningWeeklyDataForPage(pageManningData, page);
      }, page * 500);
    }
  }
  // eslint-disable-next-line
}, [manningData]);

const confirmDeleteManning = async () => {
  if (!manningToDelete) return;

  setDeleting(true);
  try {
      const manningId = encodeURIComponent(manningToDelete.manning_info_id);

      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute` +
                  `?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==` +
                  `&action=delete_manning` +
                  `&manning_id=${manningId}`;

      await fetchWithAuth(url, {
        method: 'DELETE',
      });

    // Close modal
    setDeleteConfirmOpen(false);
    setManningToDelete(null);

    // Refresh manning data
    await refreshData();
  } catch (err) {
    console.error('Error deleting manning record:', err);
  } finally {
    setDeleting(false);
  }
};


useEffect(() => {
  const pageManningData = manningData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  if (pageManningData.length > 0) {
    fetchManningWeeklyDataForPage(pageManningData, currentPage);
  }
  // eslint-disable-next-line
}, [currentPage, manningData]);

  
const mondayForWeekKey = (weekKey: string) => {
  const weekIndex = parseInt(weekKey.replace('week_', ''), 10) - 1;
  const today = new Date();
  const baseMonday = new Date(today);
  baseMonday.setDate(baseMonday.getDate() + (1 - baseMonday.getDay())); // current week Monday
  const weekStart = new Date(baseMonday);
  weekStart.setDate(baseMonday.getDate() + weekIndex * 7);
  return weekStart;
};

  const handleVariousClick = async (manningId: string, manningProjectInfoId: number | null, weekKey: string) => {
    console.log("Various click")
    try {
      setSelectedPersonId(manningId);
      setSelectedWeekKey(weekKey);

      const manningObj = manningData.find((p) => p.manning_info_id === manningId);
      setSelectedManningName(manningObj?.name || '');

      // 🔹 set the week start date for the modal header
      setProjectStartDate(mondayForWeekKey(weekKey));

      const base = `https://manningserviceapp.azurewebsites.net/api/MainRoute`;
      const qs =
        `?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==` +
        `&action=get_various_projects` +
        `&manning_id=${encodeURIComponent(manningId)}` +
        (manningProjectInfoId != null ? `&manning_project_info_id=${encodeURIComponent(String(manningProjectInfoId))}` : '');

      const url = `${base}${qs}`;
      const existingProjects = await fetchWithAuth(url);
  
      if (existingProjects && Array.isArray(existingProjects)) {
        const mapped = existingProjects.map((proj: any) => ({
          projectCode: proj.project_code,
          projectName: proj.project_name,
          utilization: proj.utilization_percentage,
        }));
  
        setSelectedProjects(mapped);
      } else {
        setSelectedProjects([]); // no existing various projects
      }
  
      setVariousModalOpen(true);
    } catch (error) {
      console.error('Error fetching existing Various Projects:', error);
    }
  };
  
  const saveVariousProjects = async () => {
    // Validate that all required fields are set
    if (!selectedPersonId) {
      // Error if end date or selected person or projects are missing
      setError('Please select a person.');
      return; // Don't proceed if validation fails
    }

    if (selectedProjects.length === 0) {
      // Error if end date or selected person or projects are missing
      setError('Please select projects.');
      return; // Don't proceed if validation fails
    }

    if (!selectedEndDate) {
      // Error if end date or selected person or projects are missing
      setError('Please select an end date.');
      return; // Don't proceed if validation fails
    }
  
    // Reset any previous errors
    setError('');
  
  
    const totalUtilization = selectedProjects.reduce((sum, proj) => {
      const util = typeof proj.utilization === 'number' ? proj.utilization : 0;
      return sum + util;
    }, 0);
    const finalUtilization = Math.min(totalUtilization, 100);

    try {
      const manningWeeks = manningWeeklyData[selectedPersonId];
      const updatedWeeks: string[] = [];
      const targetMpiIds: number[] = [];
  
      const startWeekIndex = parseInt(selectedWeekKey.replace('week_', '')) - 1;
      const today = new Date();
      const baseMonday = new Date(today);
      baseMonday.setDate(baseMonday.getDate() + (1 - baseMonday.getDay())); // Start of the week
      const startMonday = new Date(baseMonday);
      startMonday.setDate(baseMonday.getDate() + startWeekIndex * 7); // Calculate the start of the selected week
  
      // Loop through the Manning weeks to update each week
      for (const [key, data] of Object.entries(manningWeeks)) {
        const weekIndex = parseInt(key.replace('week_', '')) - 1;
        const weekMonday = new Date(baseMonday);
        weekMonday.setDate(baseMonday.getDate() + weekIndex * 7); // Get Monday for each week
  
        // If the week is within the selected range, update it
        if (
          normalize(weekMonday) >= normalize(startMonday) &&
          normalize(weekMonday) <= normalize(selectedEndDate) &&
          data.manning_project_info_id
        ) {
          targetMpiIds.push(data.manning_project_info_id);
  
          const payload = selectedProjects.length === 1
            ? {
                projectCode: selectedProjects[0].projectCode,
                projectName: selectedProjects[0].projectName,
                util_percentage: selectedProjects[0].utilization ?? selectedUtilization,
              }
            : {
                projectCode: "Various",
                projectName: "Various",
                util_percentage: finalUtilization,
              };
  
          // Update the project info for this week
          try {
            const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_manning_project_info`;
            await fetchWithAuth(url, {
              method: 'POST',
              body: JSON.stringify({
                manning_project_info_id: data.manning_project_info_id,
                ...payload,
              }),
            });
  
            updatedWeeks.push(key);
          } catch (error) {
            console.error(`❌ Error updating week ${key}:`, error);
          }
        }
      }
  
      // If there are multiple projects ("Various"), add them in bulk
      if (selectedProjects.length > 1 && targetMpiIds.length > 0) {
        const addUrl = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_various_project_info`;
  
        await fetchWithAuth(addUrl, {
          method: 'POST',
          body: JSON.stringify({
            manning_info_id: selectedPersonId,
            manning_project_info_ids: targetMpiIds,
            projects: selectedProjects.map((proj) => ({
              projectCode: proj.projectCode,
              projectName: proj.projectName,
              utilization: proj.utilization ?? selectedUtilization,
            })),
          }),
        });
      }
  
      // Update the weekly data in the local state
      setManningWeeklyData((prev) => {
        const updated = { ...prev };
        for (const weekKey of updatedWeeks) {
          updated[selectedPersonId][weekKey] = {
            ...updated[selectedPersonId][weekKey],
            projectCode: selectedProjects.length === 1 ? selectedProjects[0].projectCode : "Various",
            projectName: selectedProjects.length === 1 ? selectedProjects[0].projectName : "Various",
            utilization_percentage: selectedProjects.length === 1 ? selectedProjects[0].utilization! : finalUtilization,

          };
        }
        return updated;
      });
  
      // Optionally, reload the specific Manning record (not required if you're updating local state)
      // await reloadSingleManningRecord(selectedPersonId);
            // Refresh the data to reflect the changes
      await refreshDataAfterVarious();  

      // Close the modal and reset form state
      setVariousModalOpen(false);
      setSelectedProjects([]);
      setSelectedUtilization(0);
      setProjectEndDate(null);
      setSelectedEndDate(null);

 
    } catch (error) {
      console.error('Error in saveVariousProjects:', error);
    }
  };
  
      
  const reloadSingleManningRecord = async (manningId: string) => {
    try {
      // 1. Fetch updated Manning info (basic info)
      const infoUrl = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_manning_info&manning_id=${manningId}`;
      const updatedManningInfo = await fetchWithAuth(infoUrl);
  
      // 2. Fetch updated Manning weekly data (12 weeks projects)
      const weeklyUrl = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_manning_project_info_batch&manning_ids=${manningId}`;
      const updatedWeeklyData = await fetchWithAuth(weeklyUrl);
  
      // 3. Update Manning Data
      setManningData(prev => 
        prev.map(manning => 
          manning.manning_info_id === manningId ? updatedManningInfo[0] : manning
        )
      );
  
      // 4. Update Weekly Data
      const weeks: ManningWeekData = {};
      const firstMonday = parseISO(updatedWeeklyData[0]?.project_start_date);
  
      for (let i = 0; i < totalWeeks; i++) {
        const weekStartDate = addWeeks(firstMonday, i);
        const formattedDate = format(weekStartDate, 'MM/dd/yyyy');
  
        const project = updatedWeeklyData[i] || {};
  
        weeks[`week_${i + 1}`] = {
          projectCode: project.project_code || null,
          projectName: project.project_name || 'No Project',
          backgroundColor: '', 
          manning_project_info_id: project.manning_project_info_id || null,
          utilization_percentage: project?.utilization_percentage || '100',
        };
      }
  
      setManningWeeklyData(prev => {
        const updated = { ...prev };
        updated[manningId] = { ...weeks }; // force clone
        return { ...updated }; // force top-level clone
      });
      
  
      console.log(`✅ Reloaded Manning info and weekly data for ${manningId}`);
    } catch (error) {
      console.error('Error reloading Manning record:', error);
    }
  };
  
  const titles = [
  "Controls Engineer",
  "Controls Specialist",
  "Director",
  "Director of PM",
  "Instrumentation & Controls Engineer",
  "Instrumentation and Controls Engineer",
  "Intern",
  "Manager, Control System Integration",
  "Manager, Control Systems Integration",
  "Manager, Electrical Engineering",
  "Manager, Engineering",
  "Manager, Process Controls",
  "Network Engineer",
  "Partner",
  "Process Engineer",
  "Project Engineer",
  "Project Manager",
  "Project Manager, Control Systems",
  "Project Manager, Electrical Engineering",
  "Software Engineer",
  "Sr. Controls Engineer",
  "Sr. Designer, Instrumentation and Controls",
  "Sr. Network Engineer",
  "Sr. Process Engineer",
  "Sr. Project Engineer",
  "Sr. Project Manager",
  "Sr. Software Engineer"
];



const downloadUtilizationReport = async () => {
  try {
    setReportDownloading(true);

    const effectiveRegion = getEffectiveRegionCode(regionCode);

    const url =
      `https://manningserviceapp.azurewebsites.net/api/MainRoute` +
      `?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==` +
      `&action=create_utilization_report` +
      `&region=${regionCode}` +
      `&weeks=${reportWeeks}` +
      `&utilization=${reportUtilization}` +
      `&practice=${encodeURIComponent(reportPractice)}` +
      `&include_current_week=${includeCurrentWeek}`;

    const tokenData = localStorage.getItem("userToken");

    const response = await fetch(url,{
      method:"GET",
      headers:{
        Accept:"application/pdf",
        ...(tokenData ? {Authorization:`Bearer ${tokenData}`} : {})
      }
    });

    if(!response.ok) throw new Error(`Report generation failed (${response.status})`);

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${effectiveRegion}_util_${reportUtilization}_${reportWeeks}weeks.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(downloadUrl);

    setReportModalOpen(false);

  } catch(error){
      console.error(error);
      alert("Unable to generate report.");
  }
  finally{
      setReportDownloading(false);
  }
};

const handleProjectFilter = async () => {
  const effectiveRegion = getEffectiveRegionCode(regionCode);
  if (!effectiveRegion || selectedOffices.length === 0 || projectFilterText.trim() === '') {
    alert("Please select a region and office(s) and enter a project name or number.");
    return;
  }

  setProjectSearchLoading(true);
  try {
    const officeCodes = selectedOffices.join('|');
    const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_filtered_manning_info_by_project&company_code=DG&region_code=${effectiveRegion}&office_codes=${officeCodes}&project_code_or_name=${encodeURIComponent(projectFilterText.trim())}`;

    const data = await fetchWithAuth(url);
    setManningData(data);
  } catch (error) {
    console.error('Error fetching filtered manning info by project:', error);
  } finally {
    setProjectSearchLoading(false);
  }
};

// Projects that should clear prior selections and default util to 0
const isBlockingProject = (label: string) => {
  const s = label.toLowerCase();
  return s.includes('pto') || s.includes('open') || s.includes('onboarding');
};

const variousProjectOptions = useMemo(
  () => projectOptions.filter(opt => !opt.label.toLowerCase().includes('forecasted')),
  [projectOptions]
);

const handleSelectAllOffices = () => {
  const allOfficeCodes = offices.map(o => o.office_code);
  setSelectedOffices(allOfficeCodes);

  if (expandedRegion) {
    fetchManningData(expandedRegion, allOfficeCodes.join('|'), false, "", "");
  }
};

const handleDeselectAllOffices = () => {
  setSelectedOffices([]);

  if (expandedRegion) {
    fetchManningData(expandedRegion, "", false, "", "");
  }
};

const skillMap = useMemo(() => {
  const map: Record<string, Set<string>> = {};

  manningSkillData.forEach((item: any) => {
    const skill = item.skill;
    const level = item.skill_level;
    const manningId = String(item.manning_info_id);

    if (!skill || !level || !manningId) return;

    const key = `${skill}|${level}`; // ✅ composite key

    if (!map[key]) {
      map[key] = new Set();
    }

    map[key].add(manningId);
  });

  return map;
}, [manningSkillData]);


const filteredManningData = useMemo(() => {
  return manningData.filter((person) => {

    const experienceValue = parseInt(person.experience || "0");

    let experienceMatch = true;

    if (selectedExperience) {
      if (selectedExperience === "20+") {
        experienceMatch = experienceValue >= 20;
      } else {
        const [min, max] = selectedExperience.split("-").map(Number);
        experienceMatch = experienceValue >= min && experienceValue <= max;
      }
    }
    let skillMatch = true;
    if (selectedSkill) {
      skillMatch = skillMap[selectedSkill]?.has(String(person.manning_info_id)) ?? false;
    }


    return (
      person.name.toLowerCase().includes(quickFilterText.toLowerCase()) &&
      (!selectedLeader || person.leader_name === selectedLeader) &&
      (!selectedPractice || person.primary_practice === selectedPractice) &&
      (!selectedTitle || person.title === selectedTitle) &&
      experienceMatch && skillMatch
    );
  });
}, [
  manningData,
  quickFilterText,
  selectedLeader,
  selectedPractice,
  selectedTitle,
  selectedExperience,
  selectedSkill,
  skillMap
]);
const totalPages = Math.ceil(filteredManningData.length / pageSize);


//populateWeeksForSelectedLeader 
const populateWeeksForSelectedLeader = async () => {
  // true  = populate months 1–12 only for the first Manning ID
  // false = populate months 1–12 for every Manning ID in the queue
  const testFlag = false;

  if (!selectedLeader) return;

  const selectedPeople = filteredManningData.filter(
    (person) => person.leader_name === selectedLeader
  );

  if (selectedPeople.length === 0) {
    alert(`No manning records found for ${selectedLeader}.`);
    return;
  }

  const nextYear = new Date().getFullYear() + 1;

  // Create one API request per person, per month: 12 requests per Manning ID.
  const requests = selectedPeople.flatMap((person) =>
    Array.from({ length: 12 }, (_, index) => ({
      manningId: person.manning_info_id,
      officeCode: person.office_code,
      month: index + 1,
    }))
  );

  console.log('Populate Weeks full queue:', requests);

  console.log(
    'Unique Manning IDs in queue:',
    Array.from(new Set(requests.map((request) => request.manningId)))
  );

  // In test mode, take every request for the FIRST Manning ID.
  // This means months 1 through 12 for only one person.
  const firstManningId = requests.length > 0 ? requests[0].manningId : null;

  const requestsToRun =
    testFlag && firstManningId
      ? requests.filter((request) => request.manningId === firstManningId)
      : requests;

  if (requestsToRun.length === 0) {
    alert('No populate-week requests were generated.');
    return;
  }

  console.log(
    testFlag
      ? `TEST MODE: Running months 1–12 only for Manning ID ${firstManningId}`
      : 'LIVE MODE: Running all Manning IDs and all months 1–12',
    requestsToRun
  );

  setPopulatingWeeks(true);

  setPopulateWeeksStatus(
    testFlag
      ? `Test mode: populating all 12 months for Manning ID ${firstManningId} for ${nextYear}...`
      : `Populating ${requestsToRun.length} month requests for ${nextYear}...`
  );

  let completedRequests = 0;
  let failedRequests = 0;

  try {
    // Test mode runs sequentially. Live mode runs 5 API calls at a time.
    const concurrency = testFlag ? 1 : 5;
    const queue = [...requestsToRun];

    const worker = async () => {
      while (queue.length > 0) {
        const request = queue.shift();

        if (!request) return;

        try {
          const url =
            `https://manningserviceapp.azurewebsites.net/api/MainRoute` +
            `?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==` +
            `&action=populate_week_data` +
            `&company_code=DG` +
            `&region_code=${encodeURIComponent(
              getEffectiveRegionCode(regionCode)
            )}` +
            `&office_code=${encodeURIComponent(request.officeCode)}` +
            `&manning_id=${encodeURIComponent(request.manningId)}` +
            `&year=${nextYear}` +
            `&month=${request.month}`;

          console.log(
            `Calling API: Manning ID ${request.manningId}, Month ${request.month}`,
            url
          );

          const response = await fetchWithAuth(url);

          console.log(
            `API completed: Manning ID ${request.manningId}, Month ${request.month}`,
            response
          );
        } catch (error) {
          failedRequests += 1;

          console.error(
            `Error populating Manning ID ${request.manningId}, Month ${request.month}:`,
            error
          );
        } finally {
          completedRequests += 1;

          setPopulateWeeksStatus(
            `${testFlag ? 'Test mode: ' : ''}${completedRequests}/${
              requestsToRun.length
            } requests completed...`
          );
        }
      }
    };

    await Promise.all(
      Array.from(
        { length: Math.min(concurrency, requestsToRun.length) },
        () => worker()
      )
    );

    if (failedRequests > 0) {
      setPopulateWeeksStatus(
        `Completed with ${failedRequests} failed request(s). Check the browser console.`
      );
    } else if (testFlag) {
      setPopulateWeeksStatus(
        `Test complete: populated all 12 months for Manning ID ${firstManningId} for ${nextYear}.`
      );
    } else {
      setPopulateWeeksStatus(
        `Successfully populated ${requestsToRun.length} month requests for ${nextYear}.`
      );
    }

    // Refresh the affected people so their updated weekly data appears.
    const affectedManningIds = new Set(
      requestsToRun.map((request) => request.manningId)
    );

    const affectedPeople = selectedPeople.filter((person) =>
      affectedManningIds.has(person.manning_info_id)
    );

    if (affectedPeople.length > 0) {
      setFetchedPages(new Set());
      await fetchManningWeeklyDataForPage(affectedPeople, -1, true);
    }
  } catch (error) {
    console.error('Error populating weeks:', error);
    setPopulateWeeksStatus(
      'Unable to populate weeks. Check the browser console for details.'
    );
  } finally {
    setPopulatingWeeks(false);
  }
};
//populateWeeksForSelectedLeader end

useEffect(() => {
  const maxPage = Math.max(0, Math.ceil(filteredManningData.length / pageSize) - 1);

  if (currentPage > maxPage) {
    setCurrentPage(maxPage);
  }
}, [filteredManningData]);

useEffect(() => {
  setCurrentPage(0);
}, [quickFilterText, selectedLeader, selectedPractice, selectedTitle, selectedExperience, selectedSkill]);


useEffect(() => {
  fetchManningSkills();
}, [regionCode]); // ✅ VERY IMPORTANT

  const fetchManningSkills = async () => {
    try {
      if (!regionCode) return; // ✅ prevent invalid call

      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_all_manning_skills&region_code=${encodeURIComponent(regionCode)}`;

      const data = await fetchWithAuth(url);
      setManningSkillData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching manning skills:', error);
      setManningSkillData([]);
    }
  };

const uniqueSkillOptions = useMemo(() => {
  const unique = Array.from(
    new Set(
      manningSkillData
        .filter((item: any) => item.skill && item.skill_level)
        .map((item: any) => `${item.skill}|${item.skill_level}`)
    )
  ).sort();

  return unique.map((key) => {
    const [skill, level] = key.split("|");

    return {
      value: key, // internal value
      label: `${skill} (${level})`, // what user sees
    };
  });
}, [manningSkillData]);

const groupedSkillOptions = useMemo(() => {
  const groups: Record<string, any[]> = {};

  manningSkillData.forEach((item: any) => {
    if (!item.skill || !item.skill_level) return;

    if (!groups[item.skill]) {
      groups[item.skill] = [];
    }

    const key = `${item.skill}|${item.skill_level}`;

    groups[item.skill].push({
      value: key,
      label: item.skill_level,
    });
  });

  return Object.keys(groups).map((skill) => ({
    label: skill,
    options: groups[skill],
  }));
}, [manningSkillData]);

  return (
<div className="landing-container">
  <header className="landing-header">
    <img src={logo} alt="Design Group Logo" className="landing-logo" />
    <h1 className="landing-title">Staffing Portal - Manning</h1>
    <div className="user-icon-container"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}      
    >
      <span className="username-text">{username}</span>
      <img src={userIcon} alt="User Icon" className="user-icon" />
      {showLogout && (
      <button className="logout-button" onClick={handleLogout}>
         Logout
      </button>
      )}
    </div>
  </header>

  <Navigation
        regions={regions}
        expandedRegion={expandedRegion}
        selectedSubLink={selectedSubLink}
        toggleRegion={toggleRegion}
        setSelectedSubLink={setSelectedSubLink}
  />

  <main className="landing-main">

  <div className="filter-toggle-container" style={{ marginBottom: '15px' }}>
  <button
    onClick={() => setShowFilters((prev) => !prev)}
    style={{
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
    }}
    title="Toggle Filters"
  >
    <img
      src={filterIcon}
      alt="Filter Icon"
      style={{ width: '32px', height: '32px' }}
    />
  </button>
</div>


{showFilters && (
  <div
    className="filter-section"
    style={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      gap: '20px',
      padding: '10px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      backgroundColor: '#f9f9f9',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative',
    }}
  >
    {/* === By Utilization === */}
    
{/* === Grouped: Utilization + Title === */}
<div className="filter-column" style={{ display: 'flex', flexDirection: 'column' }}>
  {/* By Utilization */}
  <div>
    <div onClick={() => setShowUtilization((prev) => !prev)} className="filter-header">
      {showUtilization ? '▼' : '▶'} By Utilization
    </div>
    {showUtilization && (
      <div className="color-picker">
        {[{ color: '#ffffff', label: '100%' }, { color: '#70ad47', label: '75%' }, { color: '#ffe699', label: '50%' }, { color: '#ed7d31', label: '25%' }, { color: '#ffff00', label: '0%' }, { color: '#8ea9db', label: 'NoPO' }].map(({ color, label }) => (
          <div key={color} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className="color-option"
              style={{ backgroundColor: color }}
              onClick={(e) => handleColorSelect(color, label)}
            >
              <span
                style={{
                  fontSize: '10px',
                  color: color === '#ffffff' ? '#000' : '#fff',
                  fontWeight: 'bold',
                  pointerEvents: 'none',
                }}
              >
                {label}
              </span>
            </button>

            {isCalendarVisible && selectedColor === color && (
              <div
                ref={calendarRef}
                className="calendar-popup"
                style={{
                  position: 'absolute',
                  top: '50px',
                  left: '0',
                  zIndex: 1000,
                  background: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  inline
                  filterDate={(date) => date.getDay() === 1}
                  dayClassName={(date) =>
                    date.toDateString() === new Date().toDateString() ? 'custom-today' : ''
                  }
                />
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>

  {/* By Title */}
  <div style={{ marginTop: '10px' }}>
    <div onClick={() => setShowTitleFilter((prev) => !prev)} className="filter-header">
      {showTitleFilter ? '▼' : '▶'} By Title
    </div>
    {showTitleFilter && (
      <div className="dropdown-wrapper" style={{ marginTop: '6px' }}>
        <select
          value={selectedTitle || ''}
          onChange={(e) => setSelectedTitle(e.target.value || null)}
          style={{ width: '100%', padding: '5px' }}
        >
          <option value="">All</option>
          {uniqueTitles.map((title) => (
            <option key={title} value={title}>{title}</option>
          ))}
        </select>
      </div>
    )}
  </div>

  {/* By Experience */}
  <div style={{ marginTop: '10px' }}>
    <div
      onClick={() => setShowExperienceFilter((prev) => !prev)}
      className="filter-header"
    >
      {showExperienceFilter ? '▼' : '▶'} By Experience
    </div>

    {showExperienceFilter && (
      <div className="dropdown-wrapper" style={{ marginTop: '6px' }}>
        <select
          value={selectedExperience || ''}
          onChange={(e) => setSelectedExperience(e.target.value || null)}
          style={{ width: '100%', padding: '5px' }}
        >
          <option value="">All</option>
          {experienceRanges.map((exp) => (
            <option key={exp} value={exp}>
              {exp === "20+" ? "20+ Years" : `${exp} Years`}
            </option>
          ))}
        </select>
      </div>
    )}
  </div>
</div>


    {/* === By Location === */}
{showLocation && (
  <div className="filter-scroll">
    {regionCode === "NATIONAL" ? (
      <span></span>
    ) : (
      <div className="filter-checkboxes">
          <div style={{ marginBottom: '8px', display: 'flex', gap: '12px', alignItems: 'center' }}>

            <SelectAllIcon
              onClick={selectedOffices.length !== offices.length ? handleSelectAllOffices : undefined}
              title="Select All Offices"
              style={{
                width: '30px',
                height: '30px',
                cursor: selectedOffices.length === offices.length ? 'not-allowed' : 'pointer',
                opacity: selectedOffices.length === offices.length ? 0.35 : 1,
                transition: 'transform 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (selectedOffices.length !== offices.length)
                  e.currentTarget.style.transform = 'scale(1.15)';
              }}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />

            <DeselectAllIcon
              onClick={selectedOffices.length !== 0 ? handleDeselectAllOffices : undefined}
              title="Deselect All Offices"
              style={{
                width: '30px',
                height: '30px',
                cursor: selectedOffices.length === 0 ? 'not-allowed' : 'pointer',
                opacity: selectedOffices.length === 0 ? 0.35 : 1,
                transition: 'transform 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (selectedOffices.length !== 0)
                  e.currentTarget.style.transform = 'scale(1.15)';
              }}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />

          </div>

        {offices.length > 0 ? (
          offices.map((office) => (
            <label key={office.office_code} className="checkbox-label">
              <input
                type="checkbox"
                checked={selectedOffices.includes(office.office_code)}
                onChange={(e) => handleOfficeChange(office.office_code, e.target.checked)}
              />
              {office.office_name}
            </label>
          ))
        ) : (
          <p>Loading offices...</p>
        )}
      </div>
    )}
  </div>
)}

    {/* === By Leader / Practice === */}
    {/* === By Leader / Practice === */}
<div className="filter-column">
  <div
    onClick={() => setShowLeaderPractice((prev) => !prev)}
    className="filter-header"
  >
    {showLeaderPractice ? '▼' : '▶'} By Leader {regionCode !== "NATIONAL" && '/ Practice'}
  </div>
  {showLeaderPractice && (
    <>
      <div className="dropdown-wrapper">
        <label>Leader: </label>
        <select
          value={selectedLeader || ''}
          onChange={(e) => setSelectedLeader(e.target.value || null)}
        >
          <option value="">All</option>
          {uniqueLeaders.map((leader) => (
            <option key={leader} value={leader}>{leader}</option>
          ))}
        </select>
      </div>

      {/* Hide Practice filter when region_code is NATIONAL */}
      {regionCode !== "NATIONAL" && (
        <div className="dropdown-wrapper">
          <label>Practice: </label>
          <select
            value={selectedPractice || ''}
            onChange={(e) => setSelectedPractice(e.target.value || null)}
          >
            <option value="">All</option>
            {uniquePractices.map((practice) => (
              <option key={practice} value={practice}>{practice}</option>
            ))}
          </select>
        </div>
      )}
    </>
  )}

  {/* By Skill */}
<div style={{ marginTop: '10px' }}>
  <div
    onClick={() => setShowSkillFilter((prev) => !prev)}
    className="filter-header"
  >
    {showSkillFilter ? '▼' : '▶'} By Skill
  </div>

  {showSkillFilter && (
  <div style={{ marginTop: '6px' }}>
    <select
      className="skill-dropdown"
      value={selectedSkill || ''}
      onChange={(e) => setSelectedSkill(e.target.value || null)}
    >
      <option value="">Select Skill</option>

      {uniqueSkillOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
</div>

  )}
</div>
</div>


    {/* === By Project === */}
<div className="filter-column">
  <div onClick={() => setShowProjectFilter(prev => !prev)} className="filter-header">
    {showProjectFilter ? '▼' : '▶'} By Project
  </div>
  {showProjectFilter && (
<div className="project-search-container">
  <input
    type="text"
    placeholder="Enter Project Name or Number"
    value={projectFilterText}
    onChange={(e) => setProjectFilterText(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter') {
        handleProjectFilter();
      }
    }}
    className="project-search-input"
  />

  <button
    onClick={handleProjectFilter}
    className="project-search-button"
  >
    <SearchIcon />
  </button>

</div>

)}

</div>

  </div>
)}


    <div className="manning-info-container">
      <div className="search-section">
        <div className="search-input-wrapper">
          <img src={personSearch} alt="Search" className="search-icon" />

          <input
            type="text"
            placeholder="Search"
            className="search-input"
            value={quickFilterText}
            onChange={onQuickFilterChange}
          />
        </div>
          <button
          className="reports-button"
          onClick={() => setReportModalOpen(true)}
        >
          Reports
        </button>

        {selectedLeader && 
          username.toLowerCase() === 'anil.mathew@bwdesigngroup.com' && (
          <button
            className="dark-teal-refresh-button"
            onClick={populateWeeksForSelectedLeader}
            disabled={populatingWeeks}
            title={`Populate all months for ${selectedLeader} for next year`}
          >
            {populatingWeeks ? (
              <div className="spinner"></div>
            ) : (
              `Populate Weeks (${new Date().getFullYear() + 1})`
            )}
          </button>
        )}
        {populateWeeksStatus && (
          <div
            style={{
              marginTop: '8px',
              fontSize: '13px',
              color: populatingWeeks ? '#555' : '#0b6b3a',
            }}
          >
            {populateWeeksStatus}
          </div>
        )}
        <div className="dark-teal-addmanning-container">
          <button className="dark-teal-addmanning-button" onClick={handleAddNewManning}>
             Add New Manning
          </button>
          {/* Clear Filter (existing refreshData behavior) */}
          <button
            className="dark-teal-refresh-button"
            onClick={clearFilter}
            disabled={loadingClear}
            title="Clear all filters and reload"
          >
            {loadingClear ? <div className="spinner"></div> : 'Clear Filters'}
          </button>

          {/* Refresh (preserve current filters) */}
          <button
            className="dark-teal-refresh-button"
            onClick={refreshData}
            disabled={loading}
            title="Refresh data without clearing filters"
          >
            {loading ? <div className="spinner"></div> : 'Refresh'}
          </button>
        </div>

      </div> {/*search-section ends */}

      <div className="sheet-or-list-view-container" style={{ width: '100%' }}>
        {viewMode === 'sheet' ? (
          <div className="sheet-table-wrapper" style={{ overflowX: 'auto', overflowY: 'auto',maxHeight: '70vh' }}>

            {/* Display the total number of Manning records */}
            <div className="pagination-row">
            <div className="pagination-container">
              <button
                className="pagination-button"
                onClick={() =>
                  setVisibleWeekStart((prev) => Math.max(0, prev - weeksPerPage))
                }
                disabled={visibleWeekStart === 0}
              >
                ← Previous 6 Weeks
              </button>

              <button
                className="pagination-button"
                onClick={() =>
                  setVisibleWeekStart((prev) =>
                    Math.min(totalWeeks - weeksPerPage, prev + weeksPerPage)
                  )
                }
                disabled={visibleWeekStart >= totalWeeks - weeksPerPage}
              >
                Next 6 Weeks →
              </button>
            </div>

            <button
              className="help-link"
              onClick={async () => {
                try {
                  const res = await fetchWithAuthV2(
                    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
                    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_help_video_url&page=manning"
                  );
  
                  setVideoUrl(res.message.video_url);
                  setShowHelpVideo(true);
                } catch(err) {
                  const msg =
                    err instanceof Error
                      ? err.message
                      : "Error updating project discipline task.";
                  //showToast(msg, "error");
                }
              }}
            >
              ❓ Help
            </button>
            </div>

                {/* Manning Data Table */}
            <table className="sheet-table">
              <thead>
                <tr>
                  <th>Name</th>
                  {Array.from({ length: totalWeeks })
                    .slice(visibleWeekStart, visibleWeekStart + weeksPerPage)
                    .map((_, weekIndex) => {
                      const monday = new Date();
                      monday.setDate(monday.getDate() + (1 - monday.getDay()) + (weekIndex + visibleWeekStart) * 7);
                      const month = String(monday.getMonth() + 1).padStart(2, '0');
                      const day = String(monday.getDate()).padStart(2, '0');
                      const formattedDate = `${month}-${day}`;
                      return (
                        <th key={weekIndex} style={{ minWidth: '100px' }}>
                          {formattedDate}
                        </th>
                      );
                    })}
                </tr>
              </thead>
              <tbody>
                {filteredManningData
                  .slice(currentPage * pageSize, (currentPage + 1) * pageSize)
                  .map((person) => (
                    <tr key={person.manning_info_id}>
                      <td style={{ minWidth: '100px', padding: '4px 2px' }}>
                        <div   style={{display: 'flex', alignItems: 'center',  gap: '2px', maxWidth: '150px'}}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span
                          className="clickable-name"
                          style={{
                            color: "#0366d6",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: '13px', // slightly smaller than default
                            fontWeight: 500
                          }}
                          onClick={() => {
                            setEditingManning(person);
                            setIsModalOpen(true);
                            setIsEditing(true);
                          }}
                        >
                          {person.name}
                        </span>
                        <span style={{ fontSize: '11px', color: '#666' }}>
                          {person.title} - {person.primary_practice}
                        </span>
                        </div>
                          <span
                            title="Delete manning record"
                            style={{
                              cursor: 'pointer',
                              color: '#c62828',
                              fontSize: '14px',
                              marginLeft: '4px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setManningToDelete(person);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            🗑️
                          </span>
                          </div>
                      </td>
                      {Array.from({ length: totalWeeks })
                        .slice(visibleWeekStart, visibleWeekStart + weeksPerPage)
                        .map((_, weekIndex) => {
                          const realWeekIndex = weekIndex + visibleWeekStart;
                          const weekKey = `week_${realWeekIndex + 1}`;
                          const weekData = manningWeeklyData[person.manning_info_id]?.[weekKey];

                          const adjustedProjectOptions =
                            weekData?.projectCode === 'Forecasted' && weekData.projectName
                              ? projectOptions.map((opt) =>
                                  opt.value === 'Forecasted'
                                    ? {
                                        ...opt,
                                        label: `${weekData.projectName}`,
                                      }
                                    : opt
                                )
                              : projectOptions;

                          return (
                            <td key={weekKey} style={{ minWidth: '100px', padding: '0' }}>
                              <div
                                style={{
                                  backgroundColor: (() => {
                                    const selectedProjectName =
                                      projectOptions.find((opt) => opt.value === weekData?.projectCode)?.label.toLowerCase() || '';
                                    if (selectedProjectName.includes('open')) return '#ffff00'; // Yellow for "Open"
                                    if (selectedProjectName.includes('pto') || selectedProjectName.includes('leave') || selectedProjectName.includes('school')  || selectedProjectName.includes('not counted')) return '#000000'; // Black for "PTO"
                                    if (selectedProjectName.includes('onboarding')) return '#A7F3D0'; // Light green for "Onboarding"
                                    switch (weekData?.utilization_percentage?.toString()) {
                                      case '100':
                                        return '#ffffff';
                                      case '75':
                                        return '#70ad47';
                                      case '50':
                                        return '#ffe699';
                                      case '25':
                                        return '#ed7d31';
                                      case '0':
                                        return '#ffff00';
                                      case 'NoPO':
                                        return '#8ea9db';
                                      default:
                                        return '#ffffff';
                                    }
                                  })(),
                                  color: (() => {
                                    const selectedProjectName =
                                      projectOptions.find((opt) => opt.value === weekData?.projectCode)?.label.toLowerCase() || '';
                                    if (selectedProjectName.includes('pto')) return '#ffffff';
                                    if (selectedProjectName.includes('open')) return '#000000';
                                    if (selectedProjectName.includes('onboarding')) return '#000000';
                                    switch (weekData?.utilization_percentage?.toString()) {
                                      case '75':
                                      case '25':
                                        return '#ffffff';
                                      default:
                                        return '#000000';
                                    }
                                  })(),
                                  padding: '4px',
                                  borderRadius: '4px',
                                }}
                                title={weekData?.projectName || 'No Project'}
                              >
                                
                                {/* ✅ CONDITIONAL: if "Various" show link, else show Select */}
                                {weekData?.projectName === 'Various' ? (
                                  <a
                                    href="#"
                                    style={{
                                      color: '#007bff',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      fontSize: '10px',
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleVariousClick(person.manning_info_id, weekData.manning_project_info_id, weekKey);
                                    }}
                                  >
                                    Various
                                  </a>
                                ) : (
                                  <div title={weekData?.projectCode === 'Forecasted' ? `Forecasted Projects - ${weekData.projectName}`  : ''}>
                                  <Select
                                    options={adjustedProjectOptions}
                                    value={
                                      weekData
                                        ? adjustedProjectOptions.find((opt) => opt.value === weekData.projectCode) || null
                                        : null
                                    }
                                    onChange={(selectedOption) => {
                                      if (!selectedOption) return;
                                      handleProjectChange(person.manning_info_id, weekKey, selectedOption.value);
                                    }}
                                    placeholder="Select"
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    styles={{
                                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                      control: (base) => ({
                                        ...base,
                                        minHeight: '24px',
                                        height: '24px',
                                        fontSize: '10px',
                                        width: '170px',
                                        minWidth: '170px',
                                        maxWidth: '170px',
                                        backgroundColor: '#ffffff', // ✅ Ensure control is white
                                      }),
                                      menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                        backgroundColor: '#ffffff', // ✅ Background of dropdown list
                                      }),
                                      option: (base, state) => ({
                                        ...base,
                                        fontSize: '10px',
                                        backgroundColor: state.isFocused ? '#f0f0f0' : '#ffffff', // Hover color
                                        color: '#000000', // ✅ Ensure text is black
                                      }),
                                      singleValue: (base) => ({
                                        ...base,
                                        fontSize: '10px',
                                        color: '#000000',
                                      }),
                                      dropdownIndicator: (base) => ({
                                        ...base,
                                        padding: 2,
                                      }),
                                      clearIndicator: (base) => ({
                                        ...base,
                                        padding: 2,
                                      }),
                                      valueContainer: (base) => ({
                                        ...base,
                                        padding: '0px 6px',
                                      }),
                                      input: (base) => ({
                                        ...base,
                                        margin: 0,
                                        padding: 0,
                                        color: '#000000', // ✅ Input text color
                                      }),
                                    }}
                                  />
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                    </tr>
                  ))}
              </tbody>
            </table>

            {/* Pagination */}
{totalPages > 1 && (
  <div className="pagination-container">

    <button
      className="pagination-link"
      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
      disabled={currentPage === 0}
    >
      ← Previous Manning 
    </button>

    <span className="pagination-info">
      Page {currentPage + 1} of {totalPages}
    </span>

    <button
      className="pagination-link"
      onClick={() =>
        setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))
      }
      disabled={currentPage >= totalPages - 1}
    >
      Next Manning →
    </button>

  </div>
)}


          </div> 
        ) : null}
      </div>

      {projectModalOpen && (
        <Modal isOpen={projectModalOpen} onClose={() => setProjectModalOpen(false)}>
            <h3>{selectedNewProject?.projectCode === 'Forecasted' ? 'Enter Forecasted Project(s)' : 'Set Project Assignment'}</h3>

            <p>
              <strong>Name:</strong> {selectedManningName || 'N/A'} &nbsp;|&nbsp; 
              <strong>Week Start:</strong> {projectStartDate ? format(projectStartDate, 'MMMM dd, yyyy') : 'N/A'}
            </p>

            {selectedNewProject?.projectCode === 'Forecasted' ? (
              <>
                <label>Forecasted Project Name</label>
                <input
                  type="text"
                  value={forecastedProjectName}
                  onChange={(e) => setForecastedProjectName(e.target.value)}
                  placeholder="Enter project name"
                  style={{ width: '100%', marginBottom: '10px' }}
                />
              </>
            ) : (
              <p><strong>Project:</strong> {selectedNewProject?.projectName}</p>
            )}


            <label>Utilization</label>
            <select
              value={projectUtilization}
              onChange={(e) => setProjectUtilization(e.target.value)}
              style={{ width: '100%', marginBottom: '10px' }}
            >
              {['100', '75', '50', '25', '0', 'NoPO'].map((val) => (
                <option key={val} value={val}>{val}%</option>
              ))}
            </select>

            <label>Set until (End Date)</label>
            <DatePicker
              selected={projectEndDate}
              onChange={(date) => setProjectEndDate(date)}
              filterDate={(date) => date.getDay() === 1}
              inline
              calendarClassName="custom-calendar"
              openToDate={projectStartDate || new Date()}
            />


            {error && <div className="error-message">{error}</div>}
            <div className="save-button-container">
              <button onClick={saveProjectAssignment} className="save-button">  {saving ? (<><span className="spinner" style={{ marginRight: '6px' }}></span>Saving...</>) : ("Save")}</button>
              <button onClick={() => setProjectModalOpen(false)} className="save-button">Cancel</button>
            </div>
        </Modal>
      )}
      {variousModalOpen && (
        <Modal isOpen={variousModalOpen} onClose={() => setVariousModalOpen(false)}>
          <div className="various-modal">
            <h3>Select Projects</h3>

            <p>
              <strong>Name:</strong> {selectedManningName || 'N/A'} &nbsp;|&nbsp; 
              <strong>Week Start:</strong> {projectStartDate ? format(projectStartDate, 'MMMM dd, yyyy') : 'N/A'}
            </p>

<Select
  className="wide-select"
  options={variousProjectOptions}
  value={
    pendingProject
      ? {
          value: pendingProject.projectCode,
          label:
            variousProjectOptions.find((p) => p.value === pendingProject.projectCode)?.label ||
            pendingProject.projectName,
        }
      : null
  }
  onChange={(selectedOption) => {
    if (!selectedOption) {
      setPendingProject(null);
      return;
    }

    const label = selectedOption.label;

    if (isBlockingProject(label)) {
      // Clear already selected projects
      setSelectedProjects([]);

      // Set this as pending
      setPendingProject({
        projectCode: selectedOption.value,
        projectName: label,
      });

      // ✅ Pre-select utilization = 0
      setPendingUtilization(0);
      setError('');
      return;
    }

    // Normal (non-blocking) project
    setPendingProject({
      projectCode: selectedOption.value,
      projectName: label,
    });

    // reset utilization if it was 0 from before
    setPendingUtilization(null);
    setError('');
  }}
  isSearchable
  placeholder="Select a project"
/>


            <h4 className="modal-heading heading-util">Select Utilization</h4>
            <select
              className="util-select"
              value={pendingUtilization ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                setPendingUtilization(v);

                if (!pendingProject) {
                  setError('Please select a project first.');
                  return;
                }
                if (v == null) {
                  setError('Please select a utilization.');
                  return;
                }

                setSelectedProjects((prev) => [
                  ...prev,
                  {
                    projectCode: pendingProject.projectCode,
                    projectName: pendingProject.projectName,
                    utilization: v,
                  },
                ]);

                setPendingProject(null);
                setPendingUtilization(null);
                setError('');
              }}
            >
              <option value="" disabled>
                Select utilization
              </option>
              {[0, 10, 25, 40, 50, 60, 75, 90, 100].map((util) => (
                <option key={util} value={util}>
                  {util}%
                </option>
              ))}
            </select>

            <h4 className="modal-heading">Selected Projects</h4>
            {selectedProjects.length === 0 && (
              <div className="empty-projects">No projects added yet.</div>
            )}
            <div className="project-list">
              {selectedProjects.map((item, index) => (
                <div key={`${item.projectCode}-${index}`} className="project-item">
                  <span>
                    {item.projectName} — {item.utilization ?? 0}%
                  </span>
                  <button
                    onClick={() =>
                      setSelectedProjects((prev) => prev.filter((_, i) => i !== index))
                    }
                    className="remove-btn"
                    title="Remove project"
                  >
                    ❌
                  </button>
                </div>
              ))}
            </div>

            <h4 className="modal-heading">Set until (End Date)</h4>
            <DatePicker
              selected={selectedEndDate}
              onChange={handleDateChange}
              filterDate={(date) => date.getDay() === 1}
              inline
              calendarClassName="custom-calendar"
              dayClassName={(date) =>
                date.toDateString() === new Date().toDateString() ? 'custom-today' : ''
              }
            />

            {error && <div className="error-message">{error}</div>}

            <div className="modal-actions">
              <button onClick={saveVariousProjects} className="save-button">
                Save
              </button>
              <button onClick={() => setVariousModalOpen(false)} className="close-button">
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ✅ Popover shown when hovering over "Various" */}
      {showPopover && (
            <div
              className="popover-container"
              style={{
                position: 'absolute',
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                background: '#fff',
                border: '1px solid #ccc',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                borderRadius: '8px',
                padding: '10px',
                zIndex: 1000,
              }}
            >
              <h4>Help Section</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {popoverData.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <button onClick={handlePopoverClose}>Close</button>
            </div>
          )}
        </div>

        {isModalOpen && (
          <ManningModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingManning(null);
              setIsEditing(false);
            }}
            onSave={(manning) => {
              if (isEditing) {
                // Update: replace the old manning with the new one
                setManningData(prev =>
                  prev.map(p =>
                    p.manning_info_id === manning.manning_info_id ? manning : p
                  )
                );
              } else {
                // Add: just push to the array
                setManningData([...manningData, manning]);
              }
              setIsModalOpen(false);
              setEditingManning(null);
              setIsEditing(false);
            }}
            onRefresh={refreshData}
            regionCode={expandedRegion}
            uniqueLeaders={uniqueLeaders}
            editingManning={editingManning}
            isEditing={isEditing}
          />
        )}

        {selectedRowData && (
          <div ref={drawerRef}>
            <SlidingDrawer
              isOpen={isDrawerOpen}
              selectedRowData={selectedRowData}
              onClose={handleDrawerClose}
              handleInputChange={handleInputChange}
            />
          </div>
        )}

        {deleteConfirmOpen && manningToDelete && (
  <Modal
    isOpen={deleteConfirmOpen}
    onClose={() => {
      setDeleteConfirmOpen(false);
      setManningToDelete(null);
    }}
  >
    <h3>Confirm Delete</h3>

    <p>
      Do you want to delete this manning record?
    </p>

    <p>
      <strong>{manningToDelete.name}</strong> — {manningToDelete.title}
    </p>

    <div className="modal-actions">
      <button
        className="save-button"
        onClick={confirmDeleteManning}
        disabled={deleting}
      >
        {deleting ? 'Deleting...' : 'Confirm'}
      </button>

      <button
        className="close-button"
        onClick={() => {
          setDeleteConfirmOpen(false);
          setManningToDelete(null);
        }}
      >
        Cancel
      </button>
    </div>
  </Modal>
)}


        {showHelpVideo && videoUrl && (
          <div className="modal-overlay">
            <div className="modal video-modal">
              <h3>Project Help</h3>

              <video
                controls
                autoPlay
                style={{ width: "100%", borderRadius: "6px" }}
              >
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <div className="modal-actions">
                <button
                  className="button_w100 secondary"
                  onClick={() => setShowHelpVideo(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}


        {reportModalOpen && (
        <div className="report-modal-overlay">
        <div className="report-modal">

        <div className="report-modal-header">
          Download Reports for {regionCode}
        </div>

        <div className="report-subtitle">
        Generate reports by selecting the number of weeks out with utilization for different practices.
        </div>

        <div className="report-form-group">
        <div className="report-weeks-row">

        <div className="report-weeks-column">
        <label className="report-label">Number of Weeks</label>

        <select
        className="report-select"
        value={reportWeeks}
        onChange={(e)=>setReportWeeks(Number(e.target.value))}
        >
        {[3,4,5,6,7,8,9,10,11,12].map(w=>(
        <option key={w} value={w}>{w} Weeks</option>
        ))}
        </select>
        </div>

        <div className="report-checkbox-group">
        <label className="report-checkbox-label">
        <input
        type="checkbox"
        checked={includeCurrentWeek}
        onChange={(e)=>setIncludeCurrentWeek(e.target.checked)}
        />
        Include Current Week
        </label>
        </div>

        </div>
        </div>


        <div className="report-form-group">
        <label className="report-label">Utilization Count</label>

        <select
        className="report-select"
        value={reportUtilization}
        onChange={(e)=>setReportUtilization(Number(e.target.value))}
        >
        {[0,25,50,75].map(u=>(
        <option key={u} value={u}>{u}%</option>
        ))}
        </select>
        </div>


        <div className="report-form-group">
        <label className="report-label">Practice</label>

        <select
        className="report-select"
        value={reportPractice}
        onChange={(e)=>setReportPractice(e.target.value)}
        >
        <option value="CSI">CSI</option>
        <option value="IS">IS</option>
        <option value="A/E Process">A/E Process</option>
        </select>
        </div>


        <div className="report-summary-box">
        Generate report for the number of Pros {reportWeeks} weeks out
        {includeCurrentWeek && " (including this week)"} through week ending
        <strong> {getTargetWeekDate()}</strong> with utilization of
        <strong> {reportUtilization}%</strong> for practice
        <strong> {reportPractice}</strong>.
        Click Download Report to generate the report.
        </div>


        <div className="report-button-row">
        <button
        className="report-cancel-button"
        onClick={()=>setReportModalOpen(false)}
        >
        Cancel
        </button>

        <button
        className="report-download-button"
        disabled={reportDownloading}
        onClick={downloadUtilizationReport}
        >
        {reportDownloading ? "Generating..." : "Download Report"}
        </button>

        </div>

        </div>
        </div>
        )}
      </main>
    </div>
  );
};

export default ManningInfo;
